export type CarouselLayout =
  | "classic"
  | "card"
  | "split"
  | "bold-number"
  | "editorial"
  | "speech-bubble"
  | "vp-style";

export interface CarouselTemplate {
  id: string;
  name: string;
  background: string;
  titleColor: string;
  bodyColor: string;
  accentColor: string;
  fontFamily: string;
  titleFontSize: number;
  bodyFontSize: number;
  slideFooter?: string;
  /** Layout variant for visual diversity */
  layout: CarouselLayout;
  /** Secondary accent for decorative elements */
  accentColor2?: string;
  /** Cover slide background override */
  coverBackground?: string;
  /** Decorative shape color (circles, lines, blobs) */
  decorColor?: string;
  /** Title font weight override (default 800) */
  titleWeight?: number;
  /** Body font weight override (default 400) */
  bodyWeight?: number;
  /** Title text-transform */
  titleTransform?: "uppercase" | "none";
  /** ID of VP carousel style (for vp-style layout) */
  vpStyleId?: string;
}

export const CAROUSEL_TEMPLATES: CarouselTemplate[] = [
  // ── Classic layouts (text on background) ──
  {
    id: "minimalist",
    name: "Minimalistyczny",
    background: "#FFFFFF",
    titleColor: "#111111",
    bodyColor: "#333333",
    accentColor: "#6366F1",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },
  {
    id: "dark",
    name: "Dark Mode",
    background: "#0F0F0F",
    titleColor: "#FFFFFF",
    bodyColor: "#D1D5DB",
    accentColor: "#8B5CF6",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },
  {
    id: "cream",
    name: "Kremowy",
    background: "#FDF6E3",
    titleColor: "#1C1917",
    bodyColor: "#44403C",
    accentColor: "#D97706",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },

  // ── Card layouts (content in floating card) ──
  {
    id: "card-dark",
    name: "Karta Dark",
    background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)",
    titleColor: "#FFFFFF",
    bodyColor: "#CBD5E1",
    accentColor: "#818CF8",
    accentColor2: "#C084FC",
    decorColor: "rgba(129,140,248,0.15)",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 50,
    bodyFontSize: 30,
    layout: "card",
    coverBackground: "linear-gradient(145deg, #312e81 0%, #1e1b4b 100%)",
  },
  {
    id: "card-clean",
    name: "Karta Clean",
    background: "#F1F5F9",
    titleColor: "#0F172A",
    bodyColor: "#475569",
    accentColor: "#2563EB",
    accentColor2: "#3B82F6",
    decorColor: "rgba(37,99,235,0.08)",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 50,
    bodyFontSize: 30,
    layout: "card",
    coverBackground: "#FFFFFF",
  },

  // ── Bold number layouts (big number + content) ──
  {
    id: "bold-number-red",
    name: "Bold Numer",
    background: "#0A0A0A",
    titleColor: "#FFFFFF",
    bodyColor: "#A1A1AA",
    accentColor: "#EF4444",
    accentColor2: "#DC2626",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 46,
    bodyFontSize: 28,
    layout: "bold-number",
    titleWeight: 900,
    titleTransform: "uppercase",
  },
  {
    id: "bold-number-green",
    name: "Bold Numer Zielony",
    background: "#FFFFFF",
    titleColor: "#111827",
    bodyColor: "#4B5563",
    accentColor: "#059669",
    accentColor2: "#10B981",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 46,
    bodyFontSize: 28,
    layout: "bold-number",
    titleWeight: 900,
  },

  // ── Split layouts (accent strip + content area) ──
  {
    id: "split-purple",
    name: "Split Fiolet",
    background: "#FFFFFF",
    titleColor: "#1E1B4B",
    bodyColor: "#4338CA",
    accentColor: "#7C3AED",
    accentColor2: "#A78BFA",
    decorColor: "#7C3AED",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 52,
    bodyFontSize: 30,
    layout: "split",
    coverBackground: "linear-gradient(180deg, #7C3AED 0%, #4C1D95 100%)",
  },
  {
    id: "split-orange",
    name: "Split Orange",
    background: "#FFFBEB",
    titleColor: "#78350F",
    bodyColor: "#92400E",
    accentColor: "#F97316",
    accentColor2: "#FB923C",
    decorColor: "#F97316",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 52,
    bodyFontSize: 30,
    layout: "split",
    coverBackground: "linear-gradient(180deg, #F97316 0%, #EA580C 100%)",
  },

  // ── Editorial layouts (magazine style, big typography) ──
  {
    id: "editorial-mono",
    name: "Editorial Mono",
    background: "#FAFAFA",
    titleColor: "#000000",
    bodyColor: "#525252",
    accentColor: "#000000",
    accentColor2: "#737373",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 60,
    bodyFontSize: 28,
    layout: "editorial",
    titleWeight: 900,
    titleTransform: "uppercase",
  },
  {
    id: "editorial-dark",
    name: "Editorial Dark",
    background: "#18181B",
    titleColor: "#FAFAFA",
    bodyColor: "#A1A1AA",
    accentColor: "#FAFAFA",
    accentColor2: "#71717A",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 60,
    bodyFontSize: 28,
    layout: "editorial",
    titleWeight: 900,
    titleTransform: "uppercase",
  },

  // ── Speech bubble (conversational, friendly) ──
  {
    id: "bubble-blue",
    name: "Bąbelek Niebieski",
    background: "#EFF6FF",
    titleColor: "#1E3A5F",
    bodyColor: "#1E40AF",
    accentColor: "#3B82F6",
    accentColor2: "#93C5FD",
    decorColor: "rgba(59,130,246,0.1)",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 48,
    bodyFontSize: 30,
    layout: "speech-bubble",
  },
  {
    id: "bubble-dark",
    name: "Bąbelek Dark",
    background: "#111827",
    titleColor: "#F9FAFB",
    bodyColor: "#D1D5DB",
    accentColor: "#6366F1",
    accentColor2: "#818CF8",
    decorColor: "rgba(99,102,241,0.12)",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 48,
    bodyFontSize: 30,
    layout: "speech-bubble",
  },

  // ── Legacy gradient templates ──
  {
    id: "gradient-purple",
    name: "Gradient Fiolet",
    background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
    titleColor: "#FFFFFF",
    bodyColor: "#F0E6FF",
    accentColor: "#FDE68A",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },
  {
    id: "gradient-ocean",
    name: "Gradient Ocean",
    background: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)",
    titleColor: "#FFFFFF",
    bodyColor: "#E0F2FE",
    accentColor: "#FDE68A",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },
  {
    id: "warm",
    name: "Ciepły",
    background: "linear-gradient(135deg, #F97316 0%, #EC4899 100%)",
    titleColor: "#FFFFFF",
    bodyColor: "#FFF1F2",
    accentColor: "#FDE68A",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 56,
    bodyFontSize: 32,
    layout: "classic",
  },

  // ── VP Premium Styles (carousel-styles engine) ──
  {
    id: "vp-noir-editorial",
    name: "🔥 Noir Editorial",
    background: "#0A0A0A",
    titleColor: "#FFFFFF",
    bodyColor: "#888888",
    accentColor: "#E84C2B",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 38,
    bodyFontSize: 14,
    layout: "vp-style",
    vpStyleId: "noir-editorial",
  },
  {
    id: "vp-data-driven",
    name: "🔥 Data Driven",
    background: "#0D0D10",
    titleColor: "#FFFFFF",
    bodyColor: "#888888",
    accentColor: "#E84C2B",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 30,
    bodyFontSize: 12,
    layout: "vp-style",
    vpStyleId: "data-driven",
  },
  {
    id: "vp-bold-cards",
    name: "🔥 Bold Cards",
    background: "#08080A",
    titleColor: "#FFFFFF",
    bodyColor: "#888888",
    accentColor: "#E84C2B",
    fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
    titleFontSize: 34,
    bodyFontSize: 13,
    layout: "vp-style",
    vpStyleId: "bold-cards",
  },
];

export const SLIDE_WIDTH = 1080;
export const SLIDE_HEIGHT = 1350;
// Default preview scale. 0.22 made the on-slide copy unreadable in the side
// panel; 0.32 gives roughly 346×432 px, which keeps the slajd inside the
// panel while leaving body text legible. The "Duży" toggle still doubles it.
export const PREVIEW_SCALE = 0.32;
